import React, { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Box, Collapse, CssBaseline, Divider, Drawer, IconButton, List,
  ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography, Button, Avatar,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon, AccountBalance as AccountBalanceIcon,
  AssignmentReturn as AssignmentReturnIcon,
  Book as BookIcon, CurrencyExchange as CurrencyExchangeIcon,
  Dashboard as DashboardIcon, ExpandLess, ExpandMore,
  DeveloperMode as DeveloperModeIcon,
  ExitToApp as ExitToAppIcon, Gavel as GavelIcon, Groups as GroupsIcon,
  MonetizationOn as MonetizationOnIcon,
  Menu as MenuIcon,
  MoneyOff as MoneyOffIcon,
  LocalOffer as LocalOfferIcon,
  Payments as PaymentsIcon, People as PeopleIcon,
  Person as PersonIcon, PowerSettingsNew as PowerSettingsNewIcon,
  Receipt as ReceiptIcon, ReportProblem as ReportProblemIcon,
  Science as ScienceIcon, Security as SecurityIcon,
  Settings as SettingsIcon, Store as StoreIcon, VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';

import { canAccessPage, PageKey } from '@/auth/accessMatrix';
import { hasTraderFullAccess } from '@/auth/traderAccess';
import { hasExceptionalSupportShiftAccess } from '@/auth/supportShiftAccess';
import { RootState } from '@/store';
import { RoleEnum } from '@/types/auth/roles';
import { authService } from '../services';
import { ProfileDialog } from './ProfileDialog';

const drawerWidth = 280;

type MenuItem = {
  text: string;
  icon: React.ReactNode;
  path: string;
  page: PageKey;
  sidebarRoles?: RoleEnum[];
};

type MenuGroup = {
  id: string;
  title: string;
  icon: React.ReactNode;
  items: MenuItem[];
};

const menuGroups: MenuGroup[] = [
  {
    id: 'overview', title: 'Обзор', icon: <DashboardIcon />,
    items: [{ text: 'Дашборд', icon: <DashboardIcon />, path: '/dashboard', page: PageKey.DASHBOARD }],
  },
  {
    id: 'operations', title: 'Операции', icon: <ReceiptIcon />,
    items: [
      { text: 'Транзакции', icon: <ReceiptIcon />, path: '/transactions', page: PageKey.TRANSACTIONS },
      { text: 'Апелляции', icon: <ReportProblemIcon />, path: '/appeals', page: PageKey.APPEALS },
      { text: 'Выплаты', icon: <PaymentsIcon />, path: '/payouts-out', page: PageKey.PAYOUTS_OUT },
      { text: 'Chargeback и возвраты', icon: <AssignmentReturnIcon />, path: '/refund-requests', page: PageKey.REFUND_REQUESTS },
      { text: 'Штрафы', icon: <GavelIcon />, path: '/penalties', page: PageKey.PENALTIES },
      { text: 'Потери', icon: <MoneyOffIcon />, path: '/losses', page: PageKey.LOSSES },
      { text: 'Смены', icon: <AccessTimeIcon />, path: '/support-shifts', page: PageKey.SUPPORT_SHIFTS, sidebarRoles: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER] },
    ],
  },
  {
    id: 'participants', title: 'Участники', icon: <GroupsIcon />,
    items: [
      { text: 'Пользователи', icon: <PeopleIcon />, path: '/users', page: PageKey.USERS },
      { text: 'Мерчанты', icon: <StoreIcon />, path: '/merchants', page: PageKey.MERCHANTS },
      { text: 'Трейдеры', icon: <PersonIcon />, path: '/traders', page: PageKey.TRADERS },
      { text: 'Верификация', icon: <SecurityIcon />, path: '/verification', page: PageKey.VERIFICATION, sidebarRoles: [RoleEnum.ADMIN] },
    ],
  },
  {
    id: 'requisites', title: 'Реквизиты и доступы', icon: <AccountBalanceIcon />,
    items: [
      { text: 'Реквизиты', icon: <AccountBalanceIcon />, path: '/requisites', page: PageKey.REQUISITES },
      { text: 'Sandbox-реквизиты', icon: <ScienceIcon />, path: '/sandbox-requisites', page: PageKey.SANDBOX_REQUISITES },
      { text: 'Invite-токены', icon: <VpnKeyIcon />, path: '/invite-tokens', page: PageKey.INVITE_TOKENS },
    ],
  },
  {
    id: 'finance', title: 'Финансы', icon: <BookIcon />,
    items: [
      { text: 'Пополнения', icon: <MonetizationOnIcon />, path: '/deposits', page: PageKey.DEPOSITS },
      { text: 'Финансы', icon: <BookIcon />, path: '/finance', page: PageKey.FINANCE },
      { text: 'Офферы', icon: <LocalOfferIcon />, path: '/offers', page: PageKey.OFFERS },
      { text: 'Settlements', icon: <CurrencyExchangeIcon />, path: '/settlements', page: PageKey.SETTLEMENTS },
    ],
  },
  {
    id: 'control', title: 'Контроль и система', icon: <SecurityIcon />,
    items: [
      { text: 'Рассылка новостей', icon: <ReceiptIcon />, path: '/news-broadcast', page: PageKey.NEWS, sidebarRoles: [RoleEnum.ADMIN] },
      { text: 'Смены', icon: <AccessTimeIcon />, path: '/support-shifts', page: PageKey.SUPPORT_SHIFTS, sidebarRoles: [RoleEnum.ADMIN] },
      { text: 'Антифрод', icon: <SecurityIcon />, path: '/antifraud', page: PageKey.ANTIFRAUD },
      { text: 'Аварийное отключение', icon: <PowerSettingsNewIcon />, path: '/geo-emergency', page: PageKey.GEO_EMERGENCY },
      { text: 'Настройки', icon: <SettingsIcon />, path: '/settings', page: PageKey.SETTINGS },
    ],
  },
  {
    id: 'development', title: 'Разработка', icon: <DeveloperModeIcon />,
    items: [
      { text: 'Таблица трейдеров', icon: <PeopleIcon />, path: '/development/traders', page: PageKey.DEVELOPMENT, sidebarRoles: [RoleEnum.ADMIN] },
    ],
  },
];

const isPathActive = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [profileOpen, setProfileOpen] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);

  const visibleGroups = useMemo(
    () => menuGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          canAccessPage(user, item.page)
          && (
            !item.sidebarRoles
            || (user && item.sidebarRoles.includes(user.role))
            || hasTraderFullAccess(user)
            || (
              group.id === 'operations'
              && item.page === PageKey.SUPPORT_SHIFTS
              && hasExceptionalSupportShiftAccess(user)
            )
          ),
        ),
      }))
      .filter((group) => group.items.length > 0),
    [user],
  );

  const handleNavigation = (path: string, groupId: string) => {
    setExpandedGroups((current) => ({ ...current, [groupId]: true }));
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Typography variant="h6" noWrap sx={{ fontSize: 24, fontWeight: 700 }}>
          <Box component="span" sx={{ background: 'linear-gradient(45deg, #2196F3, #21CBF3)', backgroundClip: 'text', color: 'transparent' }}>
            Sprut
          </Box>{' '}Admin
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {visibleGroups.map((group) => {
          const containsActiveItem = group.items.some((item) => isPathActive(location.pathname, item.path));
          const open = expandedGroups[group.id] ?? containsActiveItem;
          return (
            <Box key={group.id} sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setExpandedGroups((current) => ({ ...current, [group.id]: !open }))}
                aria-expanded={open}
                sx={{ minHeight: 42, px: 2, color: containsActiveItem ? 'primary.main' : 'text.secondary' }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{group.icon}</ListItemIcon>
                <ListItemText primary={group.title} primaryTypographyProps={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }} />
                {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {group.items.map((item) => (
                    <ListItemButton
                      key={item.path}
                      onClick={() => handleNavigation(item.path, group.id)}
                      selected={isPathActive(location.pathname, item.path)}
                      sx={{ minHeight: 42, pl: 3.5, pr: 2, mx: 1, borderRadius: 1.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14 }} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>
      <Divider />
      <List sx={{ py: 1 }}>
        <ListItemButton onClick={() => authService.logout()} sx={{ mx: 1, borderRadius: 1.5 }}>
          <ListItemIcon><ExitToAppIcon /></ListItemIcon>
          <ListItemText primary="Выход" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'fixed', top: 8, left: 8, zIndex: 2000, px: 2, py: 1,
          bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1,
          transform: 'translateY(-160%)', transition: 'transform 120ms ease',
          '&:focus': { transform: 'translateY(0)' },
        }}
      >
        Перейти к содержимому
      </Box>
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` }, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <Toolbar>
          <IconButton color="inherit" aria-label="Открыть меню" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          {user && (
            <Button
              color="inherit"
              onClick={() => setProfileOpen(true)}
              startIcon={<Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main', fontSize: 14 }}>{user.username.slice(0, 1).toUpperCase()}</Avatar>}
              sx={{ color: 'text.primary', textTransform: 'none', borderRadius: 2, px: 1.25 }}
            >
              {user.username}
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, bgcolor: 'background.paper' } }}>{drawer}</Drawer>
        <Drawer variant="permanent" open sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth, bgcolor: 'background.paper', borderRightColor: 'divider' } }}>{drawer}</Drawer>
      </Box>
      <Box id="main-content" component="main" tabIndex={-1} sx={{ flexGrow: 1, minWidth: 0, px: { xs: 2, md: 3 }, py: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, bgcolor: 'background.default', minHeight: '100vh' }}>
        <Toolbar />
        <Box sx={{ width: '100%' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
